import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";

/**
 * The single axios instance. Every request in this app goes through it, so the
 * base URL, the credential policy and the 401 handling are stated once.
 *
 * baseURL matches the Go route group: internal/httpapi.Register is mounted on
 * /api/v1.
 */
export const API_BASE = "/api/v1";

/**
 * The auth endpoints are mounted at the ROOT, not under /api/v1 — see
 * api/cmd/server/routes.go. That is deliberate: AUTH_REDIRECT_URL is registered
 * with the identity provider, so it must not move when the API version does.
 *
 * These constants are therefore absolute and must NOT be composed from
 * API_BASE. Getting this wrong is what leaves sp-migrate with a login and a
 * logout button that both 404 in the browser.
 */
export const AUTH_BASE = "/auth";

/** GET this to start the OIDC login. It is a REDIRECT: navigate, never fetch. */
export const LOGIN_PATH = `${AUTH_BASE}/login`;
/** POST this to end the session. */
export const LOGOUT_PATH = `${AUTH_BASE}/logout`;

/** The request the interceptor must never bounce, or a 401 becomes a loop. */
const ME_PATH = `${AUTH_BASE}/me`;

/**
 * The error envelope internal/httpx.ErrorHandler writes:
 *   {"error":{"code":…,"message":…,"request_id":…,"details":[…]}}
 */
export interface FieldError {
  field: string;
  message: string;
}

export interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
    request_id?: string;
    details?: FieldError[];
  };
}

function isErrorEnvelope(body: unknown): body is ErrorEnvelope {
  if (typeof body !== "object" || body === null) return false;
  const inner = (body as { error?: unknown }).error;
  if (typeof inner !== "object" || inner === null) return false;
  return typeof (inner as { message?: unknown }).message === "string";
}

/**
 * A failed API call, with the server's own words attached.
 *
 * `message` is the server's message when there was one. It is NEVER replaced
 * with a friendlier invention: "the tenant credential store is not configured
 * on this deployment" is the answer the operator needs, and "Something went
 * wrong" is not.
 */
export class ApiError extends Error {
  readonly status: number | null;
  readonly code: string;
  readonly requestId: string | null;
  readonly details: FieldError[];
  readonly url: string | null;
  readonly method: string | null;

  constructor(init: {
    message: string;
    status: number | null;
    code: string;
    requestId?: string | null;
    details?: FieldError[];
    url?: string | null;
    method?: string | null;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId ?? null;
    this.details = init.details ?? [];
    this.url = init.url ?? null;
    this.method = init.method ?? null;
  }

  /** True when the failure is the caller's identity, not the request. */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/** Narrowing helper for callers that catch `unknown`. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** The HTTP status behind an unknown error, or null when it had none. */
export function statusOf(err: unknown): number | null {
  if (err instanceof ApiError) return err.status;
  if (err instanceof AxiosError) return err.response?.status ?? null;
  return null;
}

function toApiError(err: AxiosError): ApiError {
  const status = err.response?.status ?? null;
  const body: unknown = err.response?.data;
  const url = err.config?.url ?? null;
  const method = err.config?.method?.toUpperCase() ?? null;

  if (isErrorEnvelope(body)) {
    return new ApiError({
      message: body.error.message,
      status,
      code: body.error.code,
      requestId: body.error.request_id ?? null,
      details: body.error.details ?? [],
      url,
      method,
    });
  }
  // No envelope: a proxy error page, a network failure, a cancelled request.
  // The axios message is reported verbatim rather than dressed up.
  return new ApiError({
    message: err.message,
    status,
    code: status === null ? "network_error" : "unexpected_response",
    url,
    method,
  });
}

/**
 * Where the browser is sent on a 401. Split out so it can be asserted in a
 * test without navigating jsdom.
 */
export function loginUrlFor(currentPath: string): string {
  return `/login?return_to=${encodeURIComponent(currentPath)}`;
}

function shouldRedirectOnUnauthorized(config: AxiosRequestConfig | undefined): boolean {
  const url = config?.url ?? "";
  // GET /auth/me is how the app ASKS whether there is a session. Bouncing on
  // its 401 would send the login screen's own session probe to the login
  // screen, forever.
  return !url.startsWith(ME_PATH);
}

function createClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: { Accept: "application/json" },
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (!(error instanceof AxiosError)) {
        return Promise.reject(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
      const apiError = toApiError(error);
      if (
        apiError.status === 401 &&
        shouldRedirectOnUnauthorized(error.config) &&
        typeof window !== "undefined" &&
        window.location.pathname !== "/login"
      ) {
        const here = `${window.location.pathname}${window.location.search}`;
        window.location.assign(loginUrlFor(here));
      }
      return Promise.reject(apiError);
    },
  );

  return instance;
}

export const api = createClient();

/** GET returning the parsed body. Thin, so call sites read as the route they hit. */
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.get<T>(url, config);
  return res.data;
}

export async function post<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await api.post<T>(url, body, config);
  return res.data;
}

export async function put<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await api.put<T>(url, body, config);
  return res.data;
}

export async function patch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await api.patch<T>(url, body, config);
  return res.data;
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.delete<T>(url, config);
  return res.data;
}

/**
 * Go marshals a nil slice as JSON null, so `items`, `permissions`,
 * `frameworks` and friends arrive as null rather than []. Every list this app
 * renders passes through here, so no component has to defend itself.
 */
export function coerceArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

/** Same defence for the string→number maps (state_counts, reason_counts). */
export function coerceRecord(
  value: Record<string, number> | null | undefined,
): Record<string, number> {
  return value === null || value === undefined ? {} : value;
}
